import React, {createContext, ReactNode, useCallback, useContext, useMemo} from 'react'
import {PlayerColor, PlayerState} from '@colonist/game-states'
import {useUIGameUserStatesContext} from './UIGameUserStatesContext'
import {PlayerValidator} from '@colonist/game-mechanic-validators'

interface UIGamePlayersContextProps {
    getPlayerValidator: (color: PlayerColor) => PlayerValidator
}

const UIGamePlayersContext = createContext<UIGamePlayersContextProps | undefined>(undefined)

export const useUIGamePlayersContext = () => {
    const context = useContext(UIGamePlayersContext)
    if(!context) throw new Error('UIGamePlayersContext not found')
    return context
}

interface UIGamePlayersProviderProps {
    playerStates: {[key in PlayerColor]?: PlayerState}
    children: ReactNode
}

export const UIGamePlayersProvider = ({
    playerStates,
    children,
}: UIGamePlayersProviderProps) => {
    const {userStates} = useUIGameUserStatesContext()
    const playerValidatorsMap = useMemo(() => {
        const playerValidatorsMapLocal = new Map<PlayerColor, PlayerValidator>()
        for(const [colorStr, playerState] of Object.entries(playerStates)) {
            const color = parseInt(colorStr, 10) as PlayerColor
            const userState = userStates.find((state) => state.selectedColor === color)
            if(!userState) throw new Error(`User state not found for color ${color}`)

            const playerValidator = new PlayerValidator(playerState, userState)
            playerValidatorsMapLocal.set(color, playerValidator)
        }
        return playerValidatorsMapLocal
    }, [playerStates, userStates])

    const getPlayerValidator = useCallback((color: PlayerColor) => {
        const playerValidator = playerValidatorsMap.get(color)
        if(!playerValidator) throw new Error(`Player validator not found for color ${color}`)
        return playerValidator
    }, [playerValidatorsMap])

    const contextValue = useMemo(() => ({
        getPlayerValidator: getPlayerValidator,
    }), [getPlayerValidator])

    return (
        <UIGamePlayersContext.Provider value={contextValue}>
            {children}
        </UIGamePlayersContext.Provider>
    )
}
